import type { PremiseConfig, Room } from "@shared/types/foyer";

/**
 * Where to open the TV view from the CMS.
 *
 *   - dev: TV runs on its own Vite dev server at :4001
 *   - prod (Cloudflare split): the CMS and the guest TV app are SEPARATE hosts
 *     (foyer-cms.nenda.com vs foyer.nenda.com). Same-origin would open the CMS
 *     in the preview, so we map the CMS host to its TV counterpart. The auth
 *     cookie is scoped to `.nenda.com`, so it's shared across both subdomains
 *     and the preview's API calls stay authenticated.
 *   - legacy all-in-one (Fastify serves both on one origin): same-origin works.
 */
/**
 * Map the CMS host to the guest TV app host. Pure (no `window`) so it's unit
 * testable. Falls back to the same origin for the legacy all-in-one server.
 */
export function tvHostFor(
  protocol: string,
  hostname: string,
  origin: string
): string {
  // foyer-cms.pages.dev → foyer-app.pages.dev (Cloudflare Pages default hosts)
  if (hostname === "foyer-cms.pages.dev") {
    return `${protocol}//foyer-app.pages.dev`;
  }
  // foyer-cms.<zone> → foyer.<zone> (e.g. foyer-cms.nenda.com → foyer.nenda.com)
  if (hostname.startsWith("foyer-cms.")) {
    return `${protocol}//${hostname.replace(/^foyer-cms\./, "foyer.")}`;
  }
  return origin;
}

export function tvBase(): string {
  const envBase = import.meta.env.VITE_TV_BASE as string | undefined;
  if (envBase) return envBase;
  if (import.meta.env.DEV) return "http://localhost:4001";
  const { protocol, hostname, origin } = window.location;
  return tvHostFor(protocol, hostname, origin);
}

/** Opens the TV app in a borderless popup sized 1920×1080 (clamped by the
 * monitor). Browsers vary in how much chrome they strip, but `popup` + the
 * legacy flags get us as close to chromeless as a non-installed web app can
 * get. */
function openTV(url: string): void {
  // Width/height are hints - the browser will clamp to the screen.
  const features = [
    "popup",
    "width=1920",
    "height=1080",
    "toolbar=no",
    "menubar=no",
    "location=no",
    "status=no",
    "scrollbars=no",
    "resizable=yes",
  ].join(",");
  // Cache-bust so re-opening the (named) preview window actually navigates and
  // reloads it. window.open with a fixed name + the same URL just focuses the
  // existing popup without reloading, so it keeps the config it loaded before
  // the latest CMS save - the "preview stuck on the old template" bug.
  const fresh = `${url}${url.includes("?") ? "&" : "?"}t=${Date.now()}`;
  const w = window.open(fresh, "foyer-preview", features);
  if (!w) {
    // Pop-up blocked - fall back to a new tab so the click isn't wasted.
    window.open(fresh, "_blank");
    return;
  }
  // If the window was reused, window.open may have only focused it - force the
  // navigation. (Writing location on a window we opened is allowed even when the
  // preview is on a different origin.)
  try {
    w.location.replace(fresh);
  } catch {
    // Navigation race / cross-origin timing - the busted URL above already
    // triggers a reload in browsers that honour it.
  }
  w.focus();
}

const POPUP_FEATURES = [
  "popup",
  "width=1920",
  "height=1080",
  "toolbar=no",
  "menubar=no",
  "location=no",
  "status=no",
  "scrollbars=no",
  "resizable=yes",
].join(",");

/**
 * Open a premise preview in a popup and PUSH the draft config into it via
 * postMessage (`cms=1` render mode). No server fetch, no auth cookie, no cache -
 * so it always reflects the current template, even before a save and across the
 * CMS↔TV origin split. A reused popup updates live (we re-post on every open).
 */
export function openPreview(
  premiseId: string,
  rooms: Room[],
  config?: PremiseConfig,
  /** Explicit room to preview. When set, the preview shows that room's photos.
   *  When absent, the first room is used for layout but photos show a generic
   *  "no photos in a generic preview" placeholder. */
  roomId?: string
): void {
  const room = roomId ?? rooms[0]?.id ?? "preview";
  if (!config) {
    // No draft to push (e.g. the Devices view) - render from the saved config
    // the server serves, auth via the shared cookie.
    openTV(
      `${tvBase()}/?premise=${encodeURIComponent(
        premiseId
      )}&room=${encodeURIComponent(room)}&preview=1`
    );
    return;
  }
  const url = `${tvBase()}/?premise=${encodeURIComponent(
    premiseId
  )}&room=${encodeURIComponent(room)}&preview=1&cms=1`;
  const w = window.open(url, "foyer-preview", POPUP_FEATURES);
  if (!w) {
    window.open(url, "_blank");
    return;
  }
  let origin = "*";
  try {
    origin = new URL(tvBase(), window.location.href).origin;
  } catch {
    /* keep "*" */
  }
  const send = () => {
    try {
      w.postMessage({ type: "foyer-preview-config", config }, origin);
    } catch {
      /* popup closed */
    }
  };
  const onMsg = (e: MessageEvent) => {
    if (
      e.source === w &&
      (e.data as { type?: string } | null)?.type === "foyer-preview-ready"
    ) {
      send();
    }
  };
  window.addEventListener("message", onMsg);
  // Push immediately (covers a reused, already-loaded popup) and keep retrying
  // briefly until a freshly-opened popup mounts and acks "ready".
  send();
  let n = 0;
  const iv = window.setInterval(() => {
    send();
    if (++n >= 12) {
      window.clearInterval(iv);
      window.removeEventListener("message", onMsg);
    }
  }, 400);
  w.focus();
}

export function openTemplatePreview(templateId: string): void {
  openTV(`${tvBase()}/?template=${encodeURIComponent(templateId)}`);
}

/**
 * Preview what a SIGNAGE screen is playing.
 *
 * A different URL to the launcher preview, because a signage screen is not a
 * room. `?premise=X&room=Y&preview=1` addresses a room and renders the guest
 * launcher; the signage role lives on the SCREEN record, so previewing one
 * means naming the screen. The TV app answers `?signage=<screenId>` by asking
 * the operator-scoped timeline endpoint - the same resolver the panel is
 * answered with - and captions why it is empty when it is.
 *
 * `at` scrubs to another instant ("what runs on Sunday at 08:00?").
 *
 * Carries `preview=1` as well, and NOT as decoration: the edge router forks
 * `foyer.nenda.com/` to the marketing landing page for anything that does not
 * look like a TV, and its allow-list is `tv=1` / `preview=1` / a TV
 * user-agent. Without this flag a laptop opening the preview gets "Working on
 * it" and the feature reads as broken. The worker now also recognises
 * `signage`, so a hand-typed URL works, but that lives in a separately
 * deployed Worker - this keeps the button correct on its own.
 *
 * The TV app checks `signage` BEFORE the `preview=1` room launcher (which
 * needs a `room` this URL deliberately has no business naming), so the flag
 * cannot capture the route.
 */
export function openSignagePreview(
  premiseId: string,
  screenId: string,
  at?: Date
): void {
  openTV(
    `${tvBase()}/?signage=${encodeURIComponent(
      screenId
    )}&premise=${encodeURIComponent(premiseId)}&preview=1${
      at ? `&at=${encodeURIComponent(at.toISOString())}` : ""
    }`
  );
}

/**
 * Open the preview of a REAL door-sign screen: the room's actual sign - its
 * presence, staff status, policy-reduced name and the screen's orientation -
 * as the corridor panel shows it.
 *
 * Its own URL for the same reason as signage: the TV app picks the door-sign
 * renderer from the paired device's own screen id, which a preview tab does
 * not have, so the room launcher URL could only ever show the in-room TV. The
 * TV app answers `?doorsignScreen=<screenId>` from an operator-scoped route
 * (CMS cookie), checked before the launcher branch. `preview=1` is for the
 * edge router's allow-list - see `openSignagePreview`.
 */
export function openDoorSignPreview(premiseId: string, screenId: string): void {
  openTV(
    `${tvBase()}/?doorsignScreen=${encodeURIComponent(
      screenId
    )}&premise=${encodeURIComponent(premiseId)}&preview=1`
  );
}

/**
 * Preview one screen, whichever product it runs.
 *
 * The single entry point for every "Open preview" button that has a screen in
 * hand, so a screen flipped to signage cannot keep opening the launcher and
 * quietly imply the flip did not take. A door sign gets its own preview for
 * the same reason (it used to fall through to the launcher, which showed the
 * in-room TV for a corridor screen). A screen with no role, or an in-room TV,
 * keeps the launcher preview.
 */
export function openScreenPreview(args: {
  premiseId: string;
  screenId: string;
  role?: string;
  rooms?: Room[];
  roomId?: string;
  config?: PremiseConfig;
}): void {
  if (args.role === "signage") {
    openSignagePreview(args.premiseId, args.screenId);
    return;
  }
  if (args.role === "door-sign") {
    openDoorSignPreview(args.premiseId, args.screenId);
    return;
  }
  openPreview(args.premiseId, args.rooms ?? [], args.config, args.roomId);
}
